/**
 * Web app onboarding progress persistence
 *
 * Centralizes completion checks and Firestore writes for `webAppOnboarding.state`,
 * `currentStep`, `displayedBillingInfo`, and related flags on `signedUpPatients`.
 * Writes run in the background and must not block navigation or UI.
 *
 * First-touch `flags.onboardingStartedDate` and `linkAttribution` are written by
 * `getSignedUpPatientInviteContext` on welcome load. This module handles post-auth steps.
 *
 * Used from onboarding screens and install to resume flows and mark completion.
 *
 * References:
 * - `services/editSignedUpPatient.ts`
 * - `store/onboardingStore.ts`
 */

import type {
  Insurance,
  SignedUpPatient,
  WebAppOnboardingCurrentStep,
  WebAppOnboardingStateValue,
} from '@physiofit-digital/physiofit_types';
import { deleteField } from 'firebase/firestore';
import { useEffect, useRef } from 'react';
import type { InsuranceBillingType } from '../insurance/config';
import { buildDisplayedBillingInfo, canPersistDisplayedBillingInfo } from '../insurance/metaBuilders';
import { useOnboardingStore } from '../store/onboardingStore';
import { editSignedUpPatient } from './editSignedUpPatient';

/**
 * Logs persist failures in development only.
 *
 * @param {string} label Persist operation label for the console.
 * @param {unknown} error Caught rejection value.
 *
 * @return {void}
 */
function logPersistError(label: string, error: unknown): void {
  if (import.meta.env.DEV) {
    console.error(`[onboarding] ${label}`, error);
  }
}

/**
 * Runs an async Firestore patch without awaiting in the caller.
 *
 * @param {string} label Persist operation label for error logging.
 * @param {Function} work Async function that performs the patch.
 *
 * @return {void}
 */
function runOnboardingPersistInBackground(label: string, work: () => Promise<void>): void {
  void work().catch((error) => logPersistError(label, error));
}

/**
 * Whether the patient has finished web onboarding (`state === 'completed'`).
 *
 * @param {Object | undefined} patient Loaded `signedUpPatients` document.
 *
 * @return {boolean} True when onboarding is marked completed.
 */
export function isWebAppOnboardingCompleted(patient: SignedUpPatient | undefined): boolean {
  return patient?.webAppOnboarding?.state === 'completed';
}

/**
 * Whether the completed→install lock is enforced. Production only (ERS-75): on dev builds a
 * completed patient may navigate back into the flow so testers can re-walk it without re-seeding.
 * The lock itself (and `state: 'completed'`) is untouched — only the redirect is env-gated.
 *
 * A seeded demo fixture is exempt on every environment, for the same reason dev is: it exists to be
 * walked repeatedly. Exempting it here rather than skipping the completion write keeps its document
 * identical to a real patient's, which is the point of a fixture that demonstrates the real flow.
 *
 * @param {string | undefined} signedUpPatientId Invitation id of the patient being routed.
 *
 * @return {boolean} True when the redirect must fire (production, and not a demo fixture).
 */
export function isOnboardingCompletedRedirectEnforced(signedUpPatientId?: string): boolean {
  return import.meta.env.MODE === 'production' && !isDemoOnboardingInvitation(signedUpPatientId);
}

/**
 * Whether welcome should transition `invited` (or unset) to `started`.
 *
 * @param {string | undefined} state Current `webAppOnboarding.state` on the patient doc.
 *
 * @return {boolean} True for undefined or `invited` state.
 */
export function shouldMarkOnboardingStartedOnWelcome(state: WebAppOnboardingStateValue | undefined): boolean {
  return state === undefined || state === 'invited';
}

/**
 * Persists `webAppOnboarding.state` on the signed-up patient (fire-and-forget).
 *
 * @param {string} state `WebAppOnboardingStateValue` to write.
 *
 * @return {void}
 */
export function persistOnboardingState(state: WebAppOnboardingStateValue): void {
  runOnboardingPersistInBackground(`persistOnboardingState(${state})`, async () => {
    await editSignedUpPatient({ 'webAppOnboarding.state': state });
  });
}

/**
 * Persists the last shown onboarding screen (`currentStep`).
 *
 * @param {string} step `WebAppOnboardingCurrentStep` value.
 *
 * @return {void}
 */
export function persistOnboardingCurrentStep(step: WebAppOnboardingCurrentStep): void {
  runOnboardingPersistInBackground(`persistOnboardingCurrentStep(${step})`, async () => {
    await editSignedUpPatient({ 'webAppOnboarding.currentStep': step });
  });
}

/**
 * Saves this screen as the patient's last-seen onboarding step once per mount.
 *
 * @param {string} step `WebAppOnboardingCurrentStep` for the active screen.
 *
 * @return {void}
 */
export function useSaveOnboardingStep(step: WebAppOnboardingCurrentStep): void {
  const savedRef = useRef(false);

  useEffect(() => {
    if (savedRef.current) {
      return;
    }
    savedRef.current = true;
    persistOnboardingCurrentStep(step);
  }, [step]);
}

/**
 * Whether this invitation is a seeded demo fixture, recognised by "demo" in its id.
 *
 * Demo fixtures have to stay re-walkable. Production enforces the completed-to-install redirect,
 * so a fixture that reached `state: 'completed'` could only be walked once and then needed
 * re-seeding. `isOnboardingCompletedRedirectEnforced` exempts these ids instead.
 *
 * Real patients cannot match by construction: their invitation ids are UUIDs, and the hex alphabet
 * (0-9, a-f) cannot spell "demo" (verified against 3000 production ids, 2026-09-09).
 *
 * @param {string | undefined} signedUpPatientId Invitation document id.
 *
 * @return {boolean} True for a demo fixture.
 */
export function isDemoOnboardingInvitation(signedUpPatientId: string | undefined): boolean {
  return Boolean(signedUpPatientId?.toLowerCase().includes('demo'));
}

/**
 * Marks onboarding complete when install is shown or the install CTA is used (idempotent).
 *
 * @return {void}
 */
export function persistOnboardingCompletion(): void {
  runOnboardingPersistInBackground('persistOnboardingCompletion', async () => {
    const patient = useOnboardingStore.getState().signedUpPatient;
    if (!patient?.webAppOnboarding) {
      return;
    }

    const webAppOnboarding = patient.webAppOnboarding;
    const alreadyCompleted = isWebAppOnboardingCompleted(patient);
    const needsInstallStep = webAppOnboarding.currentStep !== 'install';

    if (alreadyCompleted) {
      if (!needsInstallStep) {
        return;
      }
      await editSignedUpPatient({ 'webAppOnboarding.currentStep': 'install' });
      useOnboardingStore.getState().setSignedUpPatient({
        ...patient,
        webAppOnboarding: { ...webAppOnboarding, currentStep: 'install' },
      });
      return;
    }

    const completedDate = new Date().toISOString();
    await editSignedUpPatient({
      'webAppOnboarding.state': 'completed',
      'webAppOnboarding.currentStep': 'install',
      'webAppOnboarding.flags.onboardingCompletedDate': completedDate,
    });
    useOnboardingStore.getState().setSignedUpPatient({
      ...patient,
      webAppOnboarding: {
        ...webAppOnboarding,
        state: 'completed',
        currentStep: 'install',
        flags: {
          ...webAppOnboarding.flags,
          onboardingCompletedDate: webAppOnboarding.flags?.onboardingCompletedDate ?? completedDate,
        },
      },
    });
  });
}

/**
 * @deprecated Use {@link persistOnboardingCompletion}.
 *
 * @return {void}
 */
export function persistOnboardingCompletedOnInstall(): void {
  persistOnboardingCompletion();
}

/**
 * Snapshots billing amounts shown on the invoice checkout table.
 *
 * @param {Object} insurance Selected catalog insurer.
 *
 * @return {void}
 */
export function persistDisplayedBillingInfo(insurance: Insurance): void {
  if (!canPersistDisplayedBillingInfo(insurance)) {
    return;
  }
  runOnboardingPersistInBackground('persistDisplayedBillingInfo', async () => {
    await editSignedUpPatient({
      'webAppOnboarding.displayedBillingInfo': buildDisplayedBillingInfo(insurance),
    });
  });
}

/**
 * Removes a stale checkout billing snapshot after leaving the invoice flow.
 *
 * @return {void}
 */
export function clearDisplayedBillingInfo(): void {
  runOnboardingPersistInBackground('clearDisplayedBillingInfo', async () => {
    await editSignedUpPatient({
      'webAppOnboarding.displayedBillingInfo': deleteField(),
    });
  });
}

/**
 * Clears `displayedBillingInfo` when the new insurer is not an invoice-style checkout path.
 *
 * @param {Object} insurance Newly selected catalog insurer.
 * @param {string} billingType Effective billing flow, resolved by the caller (cohort-aware) — the
 *   function no longer recomputes from the insurer, so it does not miss a BNPL cohort override.
 *
 * @return {void}
 */
export function clearDisplayedBillingInfoIfNotInvoiceCheckout(
  insurance: Insurance,
  billingType: InsuranceBillingType,
): void {
  const showsCheckoutTable =
    billingType === 'invoice' ||
    billingType === 'bnpl' ||
    billingType === 'without_abtretung_patient' ||
    billingType === 'without_abtretung_physiofit';
  if (showsCheckoutTable && canPersistDisplayedBillingInfo(insurance)) {
    return;
  }
  clearDisplayedBillingInfo();
}
